import { OpenPositionType } from "@/api-schema-enums";
import type { OpenPosition } from "@/common/api/open-position/use-open-position-search.query";
import { formatCurrency } from "@/common/helpers/format-currency";
import { formatApiDateOnlyToLocale } from "@/common/helpers/format-date";
import { DeviceType } from "@/common/hooks/use-device";
import { getCustomerDisplayName } from "@/components/data-table/predefined/customer-table/_helpers/get-customer-display-name";
import { ImplementedAccessorColumnDef } from "@/components/data-table/types";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { EmptyValue } from "@/components/ui/empty-value";
import i18next from "i18next";
import { DunningReasonCell } from "./_components/dunning-reason-cell";
import { getDaysOverdue } from "./_helpers/get-days-overdue";

type OpenPositionDunningBlock = NonNullable<OpenPosition["dunningBlock"]>;

export const openPositionColumns = {
  invoiceNumber: {
    id: "invoiceNumber",
    size: 150,
    enableSorting: true,
    header: () => i18next.t("fia:InvoiceNumber"),
    accessorFn: (row: OpenPosition) => row.invoiceNumber,
    cell: (info) => info.getValue() || <EmptyValue />,
  } as ImplementedAccessorColumnDef<OpenPosition, OpenPosition["invoiceNumber"]>,

  type: {
    id: "type",
    size: 80,
    enableSorting: true,
    align: "center",
    header: () => i18next.t("fia:FilterType"),
    accessorFn: (row: OpenPosition) => row.type,
    cell: (info) => {
      const isPayment = info.getValue() === OpenPositionType.Payment;
      return (
        <Badge
          variant={BadgeVariant.Outlined}
          colorVariant={isPayment ? BadgeColorVariant.Primary : BadgeColorVariant.Secondary}
          className="rounded-full"
        >
          {i18next.t(isPayment ? "fia:Payment" : "fia:Receivable")}
        </Badge>
      );
    },
  } as ImplementedAccessorColumnDef<OpenPosition, OpenPositionType>,

  amount: {
    id: "amount",
    size: 160,
    enableSorting: true,
    align: "right",
    header: () => i18next.t("fia:Amount"),
    accessorFn: (row: OpenPosition) => row.amount,
    cell: (info) => <span>{formatCurrency(info.getValue(), { currency: info.row.original.currency })}</span>,
  } as ImplementedAccessorColumnDef<OpenPosition, number>,

  dueDate: {
    id: "dueDate",
    size: 170,
    enableSorting: true,
    header: () => i18next.t("fia:DueDate"),
    accessorFn: (row: OpenPosition) => row,
    cell: (info) => {
      const row = info.getValue();
      if (!row.dueDate) return <EmptyValue />;
      const daysOverdue = getDaysOverdue(row.dueDate);
      return (
        <div className="flex flex-col">
          <span>{formatApiDateOnlyToLocale(row.dueDate)}</span>
          {daysOverdue > 0 && (
            <span className="text-error text-xs">{i18next.t("fia:DaysOverdueValue", { count: daysOverdue })}</span>
          )}
        </div>
      );
    },
  } as ImplementedAccessorColumnDef<OpenPosition, OpenPosition>,

  dunningLevel: {
    id: "dunningLevel",
    size: 110,
    enableSorting: true,
    align: "right",
    header: () => i18next.t("fia:DunningLevel"),
    accessorFn: (row: OpenPosition) => row.dunningLevel,
    cell: (info) => info.getValue(),
  } as ImplementedAccessorColumnDef<OpenPosition, number>,

  dunningReason: {
    id: "dunningReason",
    size: 180,
    enableSorting: false,
    header: () => i18next.t("fia:DunningReasonColumn"),
    accessorFn: (row: OpenPosition) => row.dunningBlock ?? null,
    cell: (info) => (
      <DunningReasonCell dunningBlock={info.getValue()} dunningBlockSapSync={info.row.original.dunningBlockSapSync} />
    ),
  } as ImplementedAccessorColumnDef<OpenPosition, OpenPositionDunningBlock | null>,

  dunning: {
    id: "dunning",
    size: 180,
    enableSorting: false,
    header: () => i18next.t("fia:DunningColumn"),
    accessorFn: (row: OpenPosition) => row,
    cell: (info) => {
      const { dunningLevel, dunningBlock, dunningBlockSapSync } = info.getValue();

      if (!dunningLevel && !dunningBlock && !dunningBlockSapSync) return <EmptyValue />;

      return (
        <div className="flex flex-col items-start gap-1">
          <span>{i18next.t("fia:DunningLevelValue", { level: dunningLevel })}</span>
          {(dunningBlock || dunningBlockSapSync) && (
            <DunningReasonCell dunningBlock={dunningBlock} dunningBlockSapSync={dunningBlockSapSync} />
          )}
        </div>
      );
    },
  } as ImplementedAccessorColumnDef<OpenPosition, OpenPosition>,

  branch: {
    id: "branch",
    size: 200,
    enableSorting: true,
    header: () => i18next.t("fia:Branch"),
    accessorFn: (row: OpenPosition) => row.branch ?? null,
    cell: (info) => info.getValue()?.name || <EmptyValue />,
  } as ImplementedAccessorColumnDef<OpenPosition, OpenPosition["branch"]>,

  customer: {
    id: "customer",
    size: 260,
    enableSorting: true,
    header: () => i18next.t("fia:Customer"),
    visibility: [DeviceType.Mobile, DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row: OpenPosition) => row.customer,
    cell: (info) => getCustomerDisplayName(info.getValue()) || <EmptyValue />,
  } as ImplementedAccessorColumnDef<OpenPosition, OpenPosition["customer"]>,
};
